import { client } from '@/src/api/client';
import { normalizeApiError } from '@/src/api/error-utils';
import {
  sparkVpVoiceControllerGetBrowserSessionStatus,
  sparkVpVoiceControllerFinalizeBrowserSessionRenewal,
  sparkVpVoiceControllerPlaceCall,
  sparkVpVoiceControllerPreparePersistentBrowserSession,
  sparkVpVoiceControllerRenewBrowserSession,
  sparkVpVoiceControllerRevokeBrowserSession,
  sparkVpVoiceControllerHangupCall,
  sparkVpVoiceControllerListLines,
  sparkVpVoiceControllerRejectInboundCall,
  voiceWorkspaceControllerActive,
  voiceWorkspaceControllerArtifact,
  voiceWorkspaceControllerCallFilterOptions,
  voiceWorkspaceControllerCallListProgress,
  voiceWorkspaceControllerClaimCallListItem,
  voiceWorkspaceControllerCompleteCallListItem,
  voiceWorkspaceControllerCreateCallList,
  voiceWorkspaceControllerDetail,
  voiceWorkspaceControllerDetailWorkspace,
  voiceWorkspaceControllerGetCallList,
  voiceWorkspaceControllerListArtifacts,
  voiceWorkspaceControllerListCallLists,
  voiceWorkspaceControllerListCalls,
  voiceWorkspaceControllerMissed,
  voiceWorkspaceControllerPhonePolicy,
  voiceWorkspaceControllerPreviewCallList,
  voiceWorkspaceControllerReport,
  voiceWorkspaceControllerStatus,
  voiceWorkspaceControllerTranscriptContent,
  voiceWorkspaceControllerUpdatePhonePolicy,
  voiceWorkspaceControllerUpdatePostCall,
} from '@/src/api/generated/sdk.gen';
import {
  parseBrowserOriginatePreparedCall,
  type BrowserOriginatePreparedCall,
} from './browser-originated-call-contract';
import type { BrowserOriginateProviderCapability } from './browser-originated-call-feature';
import type {
  VoiceCallFilterOptionsResponseDto,
  VoiceCallHistoryResponseDto,
  VoiceCallDetailWorkspaceResponseDto,
  VoiceCallRecordDto,
} from '@/src/api/generated/types.gen';
import type { VoicePlatformStatus } from '../../voice-platform/api';
import type {
  VoiceArtifact,
  VoiceBrowserActivationProof,
  VoiceBrowserInstallationEnsure,
  VoiceBrowserInstallationPreparing,
  VoiceBrowserMediaCredentials,
  VoiceBrowserInstallationProjection,
  VoiceBrowserSession,
  VoiceBrowserSessionRegistration,
  VoiceCallList,
  VoiceCallListItem,
  VoiceCallPlacement,
  VoiceCallParticipant,
  VoiceCallRecord,
  VoiceCallState,
  VoiceDestination,
  VoiceLine,
  VoicePersistentBrowserSession,
  VoicePhonePolicy,
  VoiceReportSummary,
} from './types';

type VoiceRejectionDetails = {
  state: string | null;
  reasonCodes: string[];
};

function voiceRejectionDetails(body: unknown): VoiceRejectionDetails {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return { state: null, reasonCodes: [] };
  }
  const details = (body as Record<string, unknown>).details;
  if (!details || typeof details !== 'object' || Array.isArray(details)) {
    return { state: null, reasonCodes: [] };
  }
  const record = details as Record<string, unknown>;
  const state = typeof record.state === 'string' ? record.state : null;
  const reasonCodes = Array.isArray(record.reasonCodes)
    ? [...new Set(record.reasonCodes.filter(
        (reason): reason is string =>
          typeof reason === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(reason),
      ))]
    : [];
  return { state, reasonCodes };
}

export class VoiceRequestRejectedError extends Error {
  readonly definitive = true;

  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null = null,
    readonly reasonCodes: readonly string[] = [],
    readonly voiceState: string | null = null,
  ) {
    super(message);
    this.name = 'VoiceRequestRejectedError';
  }
}

async function unwrap<T>(
  request: Promise<{ data?: unknown; error?: unknown }>,
  fallback = 'Voice request failed.',
): Promise<T> {
  const response = await request;
  if (response.error || response.data === undefined) {
    const details = normalizeApiError(response.error, fallback);
    const rejection = voiceRejectionDetails(details.body);
    throw new VoiceRequestRejectedError(
      details.message,
      details.statusCode ?? 500,
      details.code,
      rejection.reasonCodes,
      rejection.state,
    );
  }
  return response.data as T;
}

export type VoiceBrowserCallPreparationInput = {
  connectorId: string;
  webrtcSessionId: string;
  lineId?: string | null;
  extensionId: string;
  endpointDeviceId: string;
  destination: VoiceDestination;
  contactId?: string | null;
  companyId?: string | null;
  dealId?: string | null;
  callListItemId?: string | null;
  leaseToken?: string | null;
  idempotencyKey: string;
};

export type VoiceCallQuery = {
  search?: string;
  direction?: 'INBOUND' | 'OUTBOUND' | 'INTERNAL';
  state?: 'DIALING' | 'RINGING' | 'ANSWERED' | 'HELD' | 'ENDED' | 'FAILED';
  callStatus?: 'ANSWERED' | 'MISSED' | 'FAILED';
  missedStatus?: 'NOT_MISSED' | 'UNRESOLVED' | 'RESOLVED';
  lineId?: string;
  agentMembershipId?: string;
  contactId?: string;
  dealId?: string;
  disposition?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
};

export type VoiceCallListPreview = {
  entries: Array<{
    index: number;
    input: string;
    normalized: string | null;
    status: 'valid' | 'invalid' | 'duplicate';
    reason: string | null;
  }>;
  summary: {
    total: number;
    valid: number;
    invalid: number;
    duplicates: number;
  };
};

function normalizeCallListItem(
  value: VoiceCallListItem & Record<string, unknown>,
): VoiceCallListItem {
  return {
    ...value,
    state: value.state ?? (value.status as VoiceCallListItem['state']),
    destinationE164: value.destinationE164 ?? value.phoneNumber ?? '',
    contactId:
      value.contactId ?? (value.crmContactId as string | null | undefined),
    companyId:
      value.companyId ?? (value.crmCompanyId as string | null | undefined),
    dealId: value.dealId ?? (value.crmDealId as string | null | undefined),
    contactName:
      value.contactName ??
      (value.displayNameSnapshot as string | null | undefined),
  };
}

function normalizeCallList(
  value: VoiceCallList & Record<string, unknown>,
): VoiceCallList {
  const items = Array.isArray(value.items)
    ? value.items.map((item) =>
        normalizeCallListItem(
          item as VoiceCallListItem & Record<string, unknown>,
        ),
      )
    : undefined;
  const count = value._count?.items ?? items?.length ?? value.totalItems ?? 0;
  return {
    ...value,
    state: value.state ?? (value.status as VoiceCallList['state']),
    lineId: value.lineId ?? (value.defaultLineId as string | null | undefined),
    totalItems: count,
    completedItems:
      value.completedItems ??
      items?.filter((item) => ['COMPLETED', 'SKIPPED'].includes(item.state))
        .length ??
      0,
    items,
  };
}

function normalizeArtifact(
  value: VoiceArtifact & Record<string, unknown>,
): VoiceArtifact {
  const raw = value as Record<string, unknown>;
  const metadata =
    raw.metadata && typeof raw.metadata === 'object'
      ? (raw.metadata as Record<string, unknown>)
      : {};
  const kind = (raw.kind ?? raw.type) as VoiceArtifact['kind'];
  const status = raw.status as VoiceArtifact['status'];
  if (kind === 'TRANSCRIPT') {
    const transcript =
      raw.transcript && typeof raw.transcript === 'object'
        ? (raw.transcript as Record<string, unknown>)
        : {};
    return {
      id: String(raw.id),
      providerArtifactId: raw.providerArtifactId as string | null | undefined,
      kind,
      status,
      transcriptPath: raw.transcriptPath as string | null | undefined,
      text: ('text' in transcript ? transcript.text : raw.text) as
        | string
        | null
        | undefined,
      segments: (transcript.segments ?? raw.segments) as
        | import('./types').VoiceTranscriptSegment[]
        | undefined,
    };
  }
  return {
    id: String(raw.id),
    providerArtifactId: raw.providerArtifactId as string | null | undefined,
    kind: 'RECORDING',
    status,
    mimeType: (raw.mimeType ?? metadata.mimeType) as string | null | undefined,
    durationSeconds: (raw.durationSeconds ?? metadata.durationSeconds) as
      number | null | undefined,
    playbackPath: raw.playbackPath as string | null | undefined,
  };
}

export function createVoiceIdempotencyKey(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function responseRecord(
  value: unknown,
  label: string,
): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`Spark VP returned an invalid ${label} response.`);
  }
  return value as Record<string, unknown>;
}

function responseString(
  record: Record<string, unknown>,
  key: string,
  label: string,
): string {
  const value = record[key];
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`Spark VP returned an invalid ${label} response.`);
  }
  return value;
}

function responseNullableString(
  record: Record<string, unknown>,
  key: string,
  label: string,
): string | null {
  const value = record[key];
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`Spark VP returned an invalid ${label} response.`);
  }
  return value;
}

function parseBrowserOriginateProviderCapability(
  value: unknown,
): BrowserOriginateProviderCapability {
  const record = responseRecord(value, 'Browser-originated capability');
  return {
    schemaVersion:
      typeof record.schemaVersion === 'string' ? record.schemaVersion : null,
    contractVersion:
      typeof record.contractVersion === 'string' ? record.contractVersion : null,
    capabilities:
      Array.isArray(record.capabilities) &&
      record.capabilities.every((capability) => typeof capability === 'string')
        ? (record.capabilities as string[])
        : [],
    verified: record.verified === true,
    status: typeof record.status === 'string' ? record.status : null,
  };
}

function parseDestination(
  value: unknown,
  label: string,
  legacyE164?: unknown,
): VoiceDestination {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const destination = value as Record<string, unknown>;
    if (destination.type === 'phone_number') {
      return {
        type: 'phone_number',
        e164Number: responseString(destination, 'e164Number', label),
      };
    }
    if (destination.type === 'extension') {
      return {
        type: 'extension',
        extensionNumber: responseString(destination, 'extensionNumber', label),
      };
    }
  }
  if (typeof legacyE164 === 'string' && legacyE164.trim()) {
    return { type: 'phone_number', e164Number: legacyE164 };
  }
  throw new Error(`Spark VP returned an invalid ${label} response.`);
}

type NormalizableCallRecord =
  | VoiceCallRecordDto
  | (VoiceCallRecord & Record<string, unknown>);

const callStates: readonly VoiceCallState[] = [
  'IDLE',
  'PREPARING_CALL',
  'PREPARING_BROWSER',
  'READY',
  'DIALING',
  'RINGING',
  'ANSWERED',
  'HELD',
  'ENDED',
  'FAILED',
  'BLOCKED',
];

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function optionalNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function optionalCallDirection(
  value: unknown,
): VoiceCallRecord['direction'] | undefined {
  return value === 'INBOUND' || value === 'OUTBOUND' || value === 'INTERNAL'
    ? value
    : undefined;
}

function optionalAfterCallStatus(
  value: unknown,
): VoiceCallRecord['afterCallStatus'] | undefined {
  return value === 'NOT_REQUIRED' || value === 'PENDING' || value === 'COMPLETED'
    ? value
    : undefined;
}

function optionalMissedStatus(
  value: unknown,
): VoiceCallRecord['missedStatus'] | undefined {
  return value === 'NOT_MISSED' || value === 'UNRESOLVED' || value === 'RESOLVED'
    ? value
    : undefined;
}

function normalizeCallState(value: unknown): VoiceCallState {
  if (typeof value === 'string' && callStates.includes(value as VoiceCallState)) {
    return value as VoiceCallState;
  }
  throw new Error('Spark VP returned an invalid call activity response.');
}

function normalizeParticipants(value: unknown): VoiceCallParticipant[] | undefined {
  if (value === undefined || value === null) return undefined;
  if (!Array.isArray(value)) {
    throw new Error('Spark VP returned an invalid call activity response.');
  }
  return value.map((entry) => {
    const participant = responseRecord(entry, 'call activity');
    return {
      participantId: responseString(participant, 'participantId', 'call activity'),
      role: responseString(participant, 'role', 'call activity'),
      state: responseString(participant, 'state', 'call activity'),
      endpointDeviceId: optionalString(participant.endpointDeviceId),
      endpointType: optionalString(participant.endpointType),
      handleUserRef: optionalString(participant.handleUserRef),
      sipCorrelationId: optionalString(participant.sipCorrelationId),
      terminalReason: optionalString(participant.terminalReason),
    };
  });
}

function normalizeAvailableCommands(value: unknown): string[] | undefined {
  if (value === undefined || value === null) return undefined;
  if (!Array.isArray(value) || value.some((command) => typeof command !== 'string')) {
    throw new Error('Spark VP returned an invalid call activity response.');
  }
  return value;
}

function normalizeCallRecord(value: NormalizableCallRecord): VoiceCallRecord {
  const record = value as Record<string, unknown>;
  const legacyE164 = optionalString(record.destinationE164);
  const destination =
    record.destination === null || record.destination === undefined
      ? legacyE164
        ? { type: 'phone_number' as const, e164Number: legacyE164 }
        : null
      : parseDestination(record.destination, 'call activity', legacyE164);

  return {
    id: responseString(record, 'id', 'call activity'),
    tenantId: optionalString(record.tenantId) ?? undefined,
    connectorId: responseString(record, 'connectorId', 'call activity'),
    callSessionId: optionalString(record.callSessionId),
    clientUserRef: optionalString(record.clientUserRef),
    destination,
    destinationE164:
      destination?.type === 'phone_number'
        ? destination.e164Number
        : legacyE164,
    state: normalizeCallState(record.state),
    stateSequence: optionalNumber(record.stateSequence),
    failureCategory: optionalString(record.failureCategory),
    failureMessage: optionalString(record.failureMessage),
    startedAt: optionalString(record.startedAt),
    lastEventAt: optionalString(record.lastEventAt),
    endpointDeviceId: optionalString(record.endpointDeviceId),
    endpointType: optionalString(record.endpointType),
    endpointSelectionReason: optionalString(record.endpointSelectionReason),
    fromLineId: optionalString(record.fromLineId),
    contactId: optionalString(record.contactId),
    companyId: optionalString(record.companyId),
    dealId: optionalString(record.dealId),
    contactName: optionalString(record.contactName),
    companyName: optionalString(record.companyName),
    eventSchemaVersion: optionalString(record.eventSchemaVersion),
    direction: optionalCallDirection(record.direction),
    fromNumber: optionalString(record.fromNumber),
    toNumber: optionalString(record.toNumber),
    lineLabel: optionalString(record.lineLabel),
    callerPresentation: optionalString(record.callerPresentation),
    routeTargetType: optionalString(record.routeTargetType),
    routeTargetId: optionalString(record.routeTargetId),
    routeSlot: optionalString(record.routeSlot),
    routeDecisionReason: optionalString(record.routeDecisionReason),
    answeredAt: optionalString(record.answeredAt),
    endedAt: optionalString(record.endedAt),
    durationSeconds: optionalNumber(record.durationSeconds),
    notes: optionalString(record.notes),
    disposition: optionalString(record.disposition),
    afterCallStatus: optionalAfterCallStatus(record.afterCallStatus),
    missedStatus: optionalMissedStatus(record.missedStatus),
    participants: normalizeParticipants(record.participants),
    winningParticipantId: optionalString(record.winningParticipantId),
    winningEndpointDeviceId: optionalString(record.winningEndpointDeviceId),
    availableCommands: normalizeAvailableCommands(record.availableCommands),
    callListItemId: optionalString(record.callListItemId),
    agentTenantMembershipId: optionalString(record.agentTenantMembershipId),
    agentName: optionalString(record.agentName),
    callbackTaskId: optionalString(record.callbackTaskId),
    createdAt: optionalString(record.createdAt) ?? undefined,
    updatedAt: optionalString(record.updatedAt) ?? undefined,
  };
}

function responsePersistenceState(
  record: Record<string, unknown>,
  label: string,
): 'persisted' | 'pending' {
  const value = record.crmPersistenceState;
  if (value !== 'persisted' && value !== 'pending') {
    throw new Error(`Spark VP returned an invalid ${label} response.`);
  }
  return value;
}

function invalidBrowserSession(): never {
  throw new Error('Spark VP returned an invalid Browser session response.');
}

function isLocalHostname(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]'
  );
}

function parseBrowserSignalingUrl(value: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return invalidBrowserSession();
  }
  if (
    /\s/.test(value) ||
    !url.hostname ||
    url.username ||
    url.password ||
    url.hash ||
    (url.protocol !== 'wss:' &&
      !(url.protocol === 'ws:' && isLocalHostname(url.hostname)))
  ) {
    return invalidBrowserSession();
  }
  return value;
}

function parseBrowserSipUri(value: string): string {
  // URL does not support SIP schemes. This deliberately accepts the narrow
  // address-of-record form issued by Spark VP and rejects control characters,
  // embedded credentials, and malformed registrar hosts before credentials
  // are handed to JsSIP.
  const match =
    /^(sips?):([^@;:?\s]+)@(\[[0-9a-f:.]+\]|[a-z0-9.-]+)(?::([0-9]{1,5}))?(?:;[a-z0-9!$&'()*+,=._~:-]+)*(?:\?[a-z0-9!$&'()*+,=._~:%-]+)?$/i.exec(
      value,
    );
  if (!match) return invalidBrowserSession();
  const port = match[4] ? Number(match[4]) : null;
  if (port !== null && (port < 1 || port > 65535))
    return invalidBrowserSession();
  return value;
}

function normalizeCallRecords(values: readonly NormalizableCallRecord[]): VoiceCallRecord[] {
  const calls: VoiceCallRecord[] = [];
  for (const value of values) {
    try {
      calls.push(normalizeCallRecord(value));
    } catch {
      // Collection endpoints may contain legacy records. Discard only the
      // malformed item; do not expose its customer payload in logs.
    }
  }
  return calls;
}

function parseBrowserIceUrl(value: string): string {
  // RFC 7064/7065 URLs are not accepted by URL(), so keep this grammar narrow
  // and reject embedded credentials, fragments, whitespace, and unknown schemes.
  if (
    !/^(stun|stuns|turn|turns):(?:\[[0-9a-f:.]+\]|[a-z0-9.-]+)(?::[0-9]{1,5})?(?:\?transport=(?:udp|tcp))?$/i.test(
      value,
    )
  ) {
    return invalidBrowserSession();
  }
  return value;
}

function parseIsoDate(value: string, label: string): string {
  if (!Number.isFinite(Date.parse(value))) {
    throw new Error(`Spark VP returned an invalid ${label} response.`);
  }
  return value;
}

function parseNullableIsoDate(
  record: Record<string, unknown>,
  key: string,
  label: string,
): string | null {
  const value = responseNullableString(record, key, label);
  return value === null ? null : parseIsoDate(value, label);
}

function parseBrowserSession(value: unknown): VoiceBrowserSession {
  const record = responseRecord(value, 'Browser session');
  const iceServers = record.iceServers;
  if (!Array.isArray(iceServers)) {
    throw new Error('Spark VP returned an invalid Browser session response.');
  }
  const parsedIceServers = iceServers.map((entry) => {
    const server = responseRecord(entry, 'Browser session');
    const urls = server.urls;
    if (
      !Array.isArray(urls) ||
      urls.length === 0 ||
      !urls.every((url) => typeof url === 'string' && Boolean(url))
    ) {
      return invalidBrowserSession();
    }
    return {
      urls: (urls as string[]).map(parseBrowserIceUrl),
      ...(typeof server.username === 'string'
        ? { username: server.username }
        : {}),
      ...(typeof server.credential === 'string'
        ? { credential: server.credential }
        : {}),
    };
  });
  const registrationTtlSeconds = record.registrationTtlSeconds;
  if (
    !Number.isInteger(registrationTtlSeconds) ||
    Number(registrationTtlSeconds) <= 0
  ) {
    throw new Error('Spark VP returned an invalid Browser session response.');
  }
  if (typeof record.expiresAt !== 'string' || !record.expiresAt) {
    throw new Error('Spark VP returned an invalid Browser session response.');
  }
  return {
    sessionId: responseString(record, 'sessionId', 'Browser session'),
    signalingUrl: parseBrowserSignalingUrl(
      responseString(record, 'signalingUrl', 'Browser session'),
    ),
    sipUri: parseBrowserSipUri(
      responseString(record, 'sipUri', 'Browser session'),
    ),
    sipUsername: responseString(record, 'sipUsername', 'Browser session'),
    sipSecret: responseString(record, 'sipSecret', 'Browser session'),
    iceServers: parsedIceServers,
    mediaCredentialsExpiresAt: parseNullableIsoDate(
      record,
      'mediaCredentialsExpiresAt',
      'Browser session',
    ),
    ...(record.mediaCredentialsIssuedAt === undefined
      ? {}
      : {
          mediaCredentialsIssuedAt: parseNullableIsoDate(
            record,
            'mediaCredentialsIssuedAt',
            'Browser session',
          ),
        }),
    ...(record.mediaCredentialsTtlSeconds === undefined
      ? {}
      : {
          mediaCredentialsTtlSeconds:
            record.mediaCredentialsTtlSeconds === null
              ? null
              : Number.isInteger(record.mediaCredentialsTtlSeconds) &&
                  Number(record.mediaCredentialsTtlSeconds) > 0
                ? Number(record.mediaCredentialsTtlSeconds)
                : invalidBrowserSession(),
        }),
    registrationTtlSeconds: Number(registrationTtlSeconds),
    expiresAt: record.expiresAt,
  };
}

function parseBrowserMediaCredentials(
  value: unknown,
): VoiceBrowserMediaCredentials {
  const record = responseRecord(value, 'Browser media credentials');
  if (!Array.isArray(record.iceServers)) return invalidBrowserSession();
  const iceServers = record.iceServers.map((entry) => {
    const server = responseRecord(entry, 'Browser media credentials');
    if (
      !Array.isArray(server.urls) ||
      server.urls.length === 0 ||
      !server.urls.every((url) => typeof url === 'string' && Boolean(url))
    ) {
      return invalidBrowserSession();
    }
    return {
      urls: (server.urls as string[]).map(parseBrowserIceUrl),
      ...(typeof server.username === 'string' ? { username: server.username } : {}),
      ...(typeof server.credential === 'string' ? { credential: server.credential } : {}),
    };
  });
  const ttlSeconds = record.ttlSeconds;
  if (!Number.isInteger(ttlSeconds) || Number(ttlSeconds) < 1) {
    return invalidBrowserSession();
  }
  return {
    sessionId: responseString(record, 'sessionId', 'Browser media credentials'),
    iceServers,
    issuedAt: parseIsoDate(
      responseString(record, 'issuedAt', 'Browser media credentials'),
      'Browser media credentials',
    ),
    expiresAt: parseIsoDate(
      responseString(record, 'expiresAt', 'Browser media credentials'),
      'Browser media credentials',
    ),
    ttlSeconds: Number(ttlSeconds),
  };
}

function parseBrowserSessionRegistration(
  value: unknown,
): VoiceBrowserSessionRegistration {
  const record = responseRecord(value, 'Browser registration status');
  const status = record.status;
  if (
    ![
      'issued',
      'registered',
      'revocation_pending',
      'retiring',
      'expired',
      'revoked',
      'failed',
    ].includes(String(status))
  ) {
    throw new Error(
      'Spark VP returned an invalid Browser registration status response.',
    );
  }
  if (
    record.lastRegisteredAt !== null &&
    typeof record.lastRegisteredAt !== 'string'
  ) {
    throw new Error(
      'Spark VP returned an invalid Browser registration status response.',
    );
  }
  return {
    sessionId: responseString(
      record,
      'sessionId',
      'Browser registration status',
    ),
    status: status as VoiceBrowserSessionRegistration['status'],
    lastRegisteredAt: record.lastRegisteredAt as string | null,
  };
}

function parseBrowserInstallationStatus(
  value: unknown,
): VoiceBrowserInstallationProjection['status'] {
  const status = String(value);
  if (
    !['issued', 'registered', 'retiring', 'revoked', 'expired', 'failed'].includes(
      status,
    )
  ) {
    throw new Error(
      'Spark VP returned an invalid Browser installation response.',
    );
  }
  return status as VoiceBrowserInstallationProjection['status'];
}

function parseBrowserInstallationSession(
  value: unknown,
): VoiceBrowserInstallationProjection['pending'] {
  if (value === null || value === undefined) return null;
  const record = responseRecord(value, 'Browser installation session');
  const generation = record.generation;
  if (!Number.isInteger(generation) || Number(generation) < 1) {
    throw new Error(
      'Spark VP returned an invalid Browser installation response.',
    );
  }
  return {
    generation: Number(generation),
    sessionId: responseString(
      record,
      'sessionId',
      'Browser installation session',
    ),
    status: parseBrowserInstallationStatus(record.status),
    expiresAt: responseString(
      record,
      'expiresAt',
      'Browser installation session',
    ),
  };
}

function parseBrowserEnsureDisposition(
  value: unknown,
): VoiceBrowserInstallationProjection['ensureDisposition'] {
  if (value === null || value === undefined) return null;
  const record = responseRecord(
    value,
    'Browser installation ensure disposition',
  );
  const nextAction = record.nextAction;
  const reason = record.reason;
  const retryAfterMs = record.retryAfterMs;
  if (
    !['none', 'wait', 'retry_ensure'].includes(String(nextAction)) ||
    (reason !== null && reason !== undefined && typeof reason !== 'string')
  ) {
    throw new Error(
      'Spark VP returned an invalid Browser installation ensure disposition.',
    );
  }
  if (
    retryAfterMs !== null &&
    retryAfterMs !== undefined &&
    (!Number.isInteger(retryAfterMs) || Number(retryAfterMs) < 0)
  ) {
    throw new Error(
      'Spark VP returned an invalid Browser installation ensure disposition.',
    );
  }
  return {
    nextAction: nextAction as NonNullable<
      VoiceBrowserInstallationProjection['ensureDisposition']
    >['nextAction'],
    reason:
      reason === undefined || reason === null
        ? null
        : ((reason as string).trim() ? (reason as string) : null),
    retryAfterMs:
      retryAfterMs === undefined || retryAfterMs === null
        ? null
        : Number(retryAfterMs),
  };
}

function parseBrowserActivationLease(value: unknown) {
  if (value === null || value === undefined) return null;
  const record = responseRecord(value, 'Browser activation lease');
  if (
    (record.state !== 'ACTIVE' && record.state !== 'RELEASED') ||
    !Number.isInteger(record.ttlSeconds) ||
    Number(record.ttlSeconds) < 1
  ) {
    throw new Error(
      'Spark VP returned an invalid Browser activation lease response.',
    );
  }
  const issuedAt = parseIsoDate(
    responseString(record, 'issuedAt', 'Browser activation lease'),
    'Browser activation lease issuedAt',
  );
  const expiresAt = parseIsoDate(
    responseString(record, 'expiresAt', 'Browser activation lease'),
    'Browser activation lease expiresAt',
  );
  if (Date.parse(expiresAt) <= Date.parse(issuedAt)) {
    throw new Error(
      'Spark VP returned an invalid Browser activation lease response.',
    );
  }
  return {
    leaseId: responseString(record, 'leaseId', 'Browser activation lease'),
    state: record.state,
    issuedAt,
    expiresAt,
    ttlSeconds: Number(record.ttlSeconds),
  } as const;
}

function parseBrowserInstallationProjection(
  value: unknown,
): VoiceBrowserInstallationProjection {
  const record = responseRecord(value, 'Browser installation');
  const generation = record.generation;
  const stateVersion = record.stateVersion;
  const preparationGeneration = record.preparationGeneration;
  const lifecycleState = String(record.lifecycleState);
  if (
    !Number.isInteger(generation) ||
    Number(generation) < 1 ||
    !Number.isInteger(stateVersion) ||
    Number(stateVersion) < 0 ||
    !nonNegativeInstallationInteger(preparationGeneration) ||
    ![
      'PREPARING',
      'REGISTERING',
      'READY',
      'ROTATING',
      'DEGRADED',
      'REVOKING',
      'REVOKED',
      'DISABLED',
      'FAILED',
    ].includes(lifecycleState)
  ) {
    throw new Error(
      'Spark VP returned an invalid Browser installation response.',
    );
  }
  for (const key of [
    'lastRegisteredAt',
    'lastProviderEventId',
    'lastProviderEventAt',
  ] as const) {
    if (record[key] !== null && typeof record[key] !== 'string') {
      throw new Error(
        'Spark VP returned an invalid Browser installation response.',
      );
    }
  }
  return {
    installationId: responseString(
      record,
      'installationId',
      'Browser installation',
    ),
    browserDeviceInstanceId: responseString(
      record,
      'browserDeviceInstanceId',
      'Browser installation',
    ),
    activationId: responseNullableString(
      record,
      'activationId',
      'Browser installation',
    ),
    activationLease: parseBrowserActivationLease(record.activationLease),
    connectorId: responseString(record, 'connectorId', 'Browser installation'),
    clientUserRef: responseString(
      record,
      'clientUserRef',
      'Browser installation',
    ),
    lineId: responseNullableString(record, 'lineId', 'Browser installation'),
    extensionId: responseNullableString(
      record,
      'extensionId',
      'Browser installation',
    ),
    endpointDeviceId: responseNullableString(
      record,
      'endpointDeviceId',
      'Browser installation',
    ),
    lifecycleState:
      lifecycleState as VoiceBrowserInstallationProjection['lifecycleState'],
    generation: Number(generation),
    sessionId: responseString(record, 'sessionId', 'Browser installation'),
    status: parseBrowserInstallationStatus(record.status),
    expiresAt: responseString(record, 'expiresAt', 'Browser installation'),
    stateVersion: Number(stateVersion),
    preparationGeneration: Number(preparationGeneration),
    pending: parseBrowserInstallationSession(record.pending),
    ...(record.ensureDisposition === undefined
      ? {}
      : {
          ensureDisposition: parseBrowserEnsureDisposition(
            record.ensureDisposition,
          ),
        }),
    lastRegisteredAt: record.lastRegisteredAt as string | null,
    lastProviderEventId: record.lastProviderEventId as string | null,
    lastProviderEventAt: record.lastProviderEventAt as string | null,
  };
}

function nonNegativeInstallationInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 0;
}

function positiveInstallationInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 1;
}

function parseBrowserPreparingTransition(
  value: unknown,
): VoiceBrowserInstallationPreparing['current'] {
  const record = responseRecord(
    value,
    'Browser installation preparation transition',
  );
  if (
    !positiveInstallationInteger(record.generation) ||
    typeof record.status !== 'string' ||
    !record.status.trim()
  ) {
    throw new Error(
      'Spark VP returned an invalid Browser preparation response.',
    );
  }
  return {
    generation: Number(record.generation),
    sessionId: responseString(
      record,
      'sessionId',
      'Browser installation preparation transition',
    ),
    status: record.status,
  };
}

function parseBrowserInstallationPreparing(
  record: Record<string, unknown>,
): VoiceBrowserInstallationPreparing {
  const retryAfterMs = record.retryAfterMs;
  if (
    !nonNegativeInstallationInteger(record.preparationGeneration) ||
    !Number.isInteger(record.stateVersion) ||
    Number(record.stateVersion) < 0 ||
    !Number.isInteger(retryAfterMs) ||
    Number(retryAfterMs) < 1 ||
    record.nextAction !== 'wait'
  ) {
    throw new Error(
      'Spark VP returned an invalid Browser preparation response.',
    );
  }
  const allowedReasons = [
    'preparation_lease_active',
    'current_session_retiring',
    'pending_successor_converging',
    'provider_projection_pending',
  ];
  if (
    !allowedReasons.includes(String(record.reason)) ||
    record.browserSession !== undefined &&
    record.browserSession !== null
  ) {
    throw new Error(
      'Spark VP returned an invalid Browser preparation response.',
    );
  }
  const current =
    record.current === undefined || record.current === null
      ? undefined
      : parseBrowserPreparingTransition(record.current);
  const pending =
    record.pending === undefined || record.pending === null
      ? undefined
      : parseBrowserPreparingTransition(record.pending);
  return {
    outcome: 'preparing',
    installationId: responseString(
      record,
      'installationId',
      'Browser preparation',
    ),
    stateVersion: Number(record.stateVersion),
    preparationGeneration: Number(record.preparationGeneration),
    reason: record.reason as VoiceBrowserInstallationPreparing['reason'],
    nextAction: 'wait',
    retryAfterMs: Number(retryAfterMs),
    ...(current ? { current } : {}),
    ...(pending ? { pending } : {}),
  };
}

function parseBrowserInstallationEnsure(
  value: unknown,
): VoiceBrowserInstallationEnsure {
  const record = responseRecord(value, 'Browser installation ensure');
  if (record.outcome === 'preparing') {
    return parseBrowserInstallationPreparing(record);
  }
  if (record.outcome !== 'ready') {
    throw new Error(
      'Spark VP returned an invalid Browser installation ensure response.',
    );
  }
  const projection = parseBrowserInstallationProjection(record);
  return {
    ...projection,
    outcome: 'ready',
    browserSession:
      record.browserSession === undefined || record.browserSession === null
        ? null
        : parseBrowserSession(record.browserSession),
  };
}

function parsePersistentBrowserSession(
  value: unknown,
): VoicePersistentBrowserSession {
  const record = responseRecord(value, 'persistent Browser session');
  return {
    connectorId: responseString(
      record,
      'connectorId',
      'persistent Browser session',
    ),
    clientUserRef: responseString(
      record,
      'clientUserRef',
      'persistent Browser session',
    ),
    lineId: responseNullableString(
      record,
      'lineId',
      'persistent Browser session',
    ),
    extensionId: responseString(
      record,
      'extensionId',
      'persistent Browser session',
    ),
    endpointDeviceId: responseString(
      record,
      'endpointDeviceId',
      'persistent Browser session',
    ),
    browserSession: parseBrowserSession(record.browserSession),
    renewalPredecessorSessionId: responseNullableString(
      record,
      'renewalPredecessorSessionId',
      'persistent Browser session',
    ),
  };
}

function parseCallPlacement(
  value: unknown,
  expectedIdempotencyKey: string,
): VoiceCallPlacement {
  const record = responseRecord(value, 'call placement');
  const phase = record.phase;
  const common = {
    connectorId: responseString(record, 'connectorId', 'call placement'),
    clientUserRef: responseString(record, 'clientUserRef', 'call placement'),
    lineId: responseNullableString(record, 'lineId', 'call placement'),
    extensionId: responseString(record, 'extensionId', 'call placement'),
    destination: parseDestination(
      record.destination,
      'call placement',
      record.destinationE164,
    ),
    destinationE164:
      typeof record.destinationE164 === 'string'
        ? record.destinationE164
        : null,
    idempotencyKey: responseString(record, 'idempotencyKey', 'call placement'),
    endpointDeviceId: responseString(
      record,
      'endpointDeviceId',
      'call placement',
    ),
    endpointType: responseString(record, 'endpointType', 'call placement'),
    endpointSelectionReason: responseString(
      record,
      'endpointSelectionReason',
      'call placement',
    ),
  };
  if (common.idempotencyKey !== expectedIdempotencyKey) {
    throw new Error(
      'Spark VP returned a call placement for a different logical attempt.',
    );
  }
  if (phase === 'originated') {
    return {
      ...common,
      phase,
      callSessionId: responseString(record, 'callSessionId', 'call placement'),
      crmPersistenceState: responsePersistenceState(record, 'call placement'),
    };
  }
  if (
    phase === 'browser_session_prepared' &&
    record.callSessionId === null &&
    common.endpointType === 'browser_webrtc'
  ) {
    return {
      ...common,
      phase,
      callSessionId: null,
      endpointType: 'browser_webrtc',
      browserSession: parseBrowserSession(record.browserSession),
    };
  }
  if (
    phase === 'browser_session_ready' &&
    record.callSessionId === null &&
    common.endpointType === 'browser_webrtc'
  ) {
    return {
      ...common,
      phase,
      callSessionId: null,
      endpointType: 'browser_webrtc',
      webrtcSessionId: responseString(
        record,
        'webrtcSessionId',
        'call placement',
      ),
    };
  }
  throw new Error('Spark VP returned an invalid call placement response.');
}

function browserCallPreparationBody(input: VoiceBrowserCallPreparationInput) {
  const {
    lineId,
    contactId,
    companyId,
    dealId,
    callListItemId,
    leaseToken,
    ...requiredInput
  } = input;
  return {
    ...requiredInput,
    ...(lineId ? { lineId } : {}),
    ...(contactId ? { contactId } : {}),
    ...(companyId ? { companyId } : {}),
    ...(dealId ? { dealId } : {}),
    ...(callListItemId ? { callListItemId } : {}),
    ...(leaseToken ? { leaseToken } : {}),
  };
}

function browserActivationHeaders(proof: VoiceBrowserActivationProof) {
  return {
    'Content-Type': 'application/json',
    'x-browser-activation-lease-id': proof.leaseId,
    'x-browser-tab-id': proof.tabId,
    'x-browser-activation-reclaim-token': proof.reclaimToken,
  };
}

export const voiceApi = {
  getStatus(signal?: AbortSignal): Promise<VoicePlatformStatus> {
    return unwrap<VoicePlatformStatus>(
      voiceWorkspaceControllerStatus({ signal }),
    );
  },

  ensureBrowserInstallation(input: {
    browserDeviceInstanceId: string;
    activationId: string;
    tabId: string;
  }): Promise<VoiceBrowserInstallationEnsure> {
    return unwrap<unknown>(
      client.put({
        url: '/contact-center/voice/browser-installations/{browserDeviceInstanceId}',
        path: { browserDeviceInstanceId: input.browserDeviceInstanceId },
        security: [{ scheme: 'bearer', type: 'http' }],
        headers: { 'Content-Type': 'application/json' },
        body: { activationId: input.activationId, tabId: input.tabId },
      }),
    ).then(parseBrowserInstallationEnsure);
  },

  getBrowserInstallation(
    installationId: string,
    signal?: AbortSignal,
  ): Promise<VoiceBrowserInstallationProjection> {
    return unwrap<unknown>(
      client.get({
        url: '/contact-center/voice/browser-installations/{installationId}',
        path: { installationId },
        security: [{ scheme: 'bearer', type: 'http' }],
        signal,
      }),
    ).then(parseBrowserInstallationProjection);
  },

  submitBrowserRegistrationObservation(
    input: {
      installationId: string;
      generation: number;
      sessionId: string;
      clientObservationId: string;
      observedAt: string;
    },
    signal?: AbortSignal,
  ): Promise<import('./types').BrowserRegistrationObservationResult> {
    return unwrap<import('./types').BrowserRegistrationObservationResult>(
      client.post({
        url: '/contact-center/voice/browser-installations/{installationId}/registration-observations',
        path: { installationId: input.installationId },
        security: [{ scheme: 'bearer', type: 'http' }],
        headers: { 'Content-Type': 'application/json' },
        body: {
          observationType: 'local_registered',
          generation: input.generation,
          sessionId: input.sessionId,
          clientObservationId: input.clientObservationId,
          observedAt: input.observedAt,
        },
        signal,
      }),
    );
  },

  logoutBrowserInstallation(installationId: string): Promise<void> {
    return unwrap<unknown>(
      client.post({
        url: '/contact-center/voice/browser-installations/{installationId}/logout',
        path: { installationId },
        security: [{ scheme: 'bearer', type: 'http' }],
        headers: { 'Content-Type': 'application/json' },
        body: { reason: 'explicit_user_logout' },
      }),
    ).then(() => undefined);
  },

  claimBrowserActivationLease(input: {
    installationId: string;
    tabId: string;
    reclaimToken: string;
  }) {
    return unwrap<unknown>(
      client.post({
        url: '/contact-center/voice/browser-installations/{installationId}/activation',
        path: { installationId: input.installationId },
        security: [{ scheme: 'bearer', type: 'http' }],
        headers: { 'Content-Type': 'application/json' },
        body: { tabId: input.tabId, reclaimToken: input.reclaimToken },
      }),
    ).then(parseBrowserActivationLease);
  },

  reconcileBrowserInstallation(installationId: string) {
    return unwrap<unknown>(
      client.post({
        url: '/contact-center/voice/browser-installations/{installationId}/reconcile',
        path: { installationId },
        security: [{ scheme: 'bearer', type: 'http' }],
      }),
    ).then(parseBrowserInstallationProjection);
  },

  renewBrowserActivationLease(input: {
    leaseId: string;
    tabId: string;
    reclaimToken: string;
  }) {
    return unwrap<unknown>(
      client.post({
        url: '/contact-center/voice/browser-activation-leases/{leaseId}/renew',
        path: { leaseId: input.leaseId },
        security: [{ scheme: 'bearer', type: 'http' }],
        headers: { 'Content-Type': 'application/json' },
        body: { tabId: input.tabId, reclaimToken: input.reclaimToken },
      }),
    ).then(parseBrowserActivationLease);
  },

  releaseBrowserActivationLease(input: {
    leaseId: string;
    tabId: string;
    reclaimToken: string;
  }) {
    return unwrap<unknown>(
      client.post({
        url: '/contact-center/voice/browser-activation-leases/{leaseId}/release',
        path: { leaseId: input.leaseId },
        security: [{ scheme: 'bearer', type: 'http' }],
        headers: { 'Content-Type': 'application/json' },
        body: { tabId: input.tabId, reclaimToken: input.reclaimToken },
      }),
    ).then(parseBrowserActivationLease);
  },

  preparePersistentBrowserSession(input: {
    browserDeviceInstanceId: string;
    activationProof: VoiceBrowserActivationProof;
  }): Promise<VoicePersistentBrowserSession> {
    return unwrap<unknown>(
      sparkVpVoiceControllerPreparePersistentBrowserSession({
        headers: browserActivationHeaders(input.activationProof),
        body: { browserDeviceInstanceId: input.browserDeviceInstanceId },
      }),
    ).then(parsePersistentBrowserSession);
  },

  /**
   * Place an outbound call through Spark VP's authoritative endpoint choice.
   * A hardphone result carries no Browser credential; Browser fallback carries
   * one in-memory, one-time session for JsSIP. A client-generated idempotency
   * key makes retries dedupe on Spark VP instead of double-dialing.
   */
  placeCall(input: {
    connectorId: string;
    destination: VoiceDestination;
    lineId?: string | null;
    contactId?: string | null;
    companyId?: string | null;
    dealId?: string | null;
    callListItemId?: string | null;
    leaseToken?: string | null;
    activeBrowserSessionId?: string;
    activationProof?: VoiceBrowserActivationProof;
    idempotencyKey?: string;
  }): Promise<VoiceCallPlacement> {
    const idempotencyKey = input.idempotencyKey ?? createVoiceIdempotencyKey();
    return unwrap<unknown>(
      sparkVpVoiceControllerPlaceCall({
        headers: input.activationProof
          ? browserActivationHeaders(input.activationProof)
          : { 'Content-Type': 'application/json' },
        body: {
          connectorId: input.connectorId,
          destination: input.destination,
          ...(input.lineId ? { lineId: input.lineId } : {}),
          ...(input.contactId ? { contactId: input.contactId } : {}),
          ...(input.companyId ? { companyId: input.companyId } : {}),
          ...(input.dealId ? { dealId: input.dealId } : {}),
          ...(input.callListItemId
            ? { callListItemId: input.callListItemId }
            : {}),
          ...(input.leaseToken ? { leaseToken: input.leaseToken } : {}),
          ...(input.activeBrowserSessionId
            ? { activeBrowserSessionId: input.activeBrowserSessionId }
            : {}),
          idempotencyKey,
        },
      }),
    ).then((result) => parseCallPlacement(result, idempotencyKey));
  },

  refreshBrowserMediaCredentials(
    sessionId: string,
    activationProof: VoiceBrowserActivationProof,
  ): Promise<VoiceBrowserMediaCredentials> {
    return unwrap<unknown>(
      client.post({
        url: '/contact-center/voice/browser-sessions/{sessionId}/media-credentials',
        path: { sessionId },
        security: [{ scheme: 'bearer', type: 'http' }],
        headers: browserActivationHeaders(activationProof),
        body: {},
      }),
    ).then(parseBrowserMediaCredentials);
  },

  getBrowserSessionStatus(
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<VoiceBrowserSessionRegistration> {
    return unwrap<unknown>(
      sparkVpVoiceControllerGetBrowserSessionStatus({
        path: { sessionId },
        signal,
      }),
    ).then(parseBrowserSessionRegistration);
  },

  getBrowserOriginateCapability(): Promise<BrowserOriginateProviderCapability> {
    return unwrap<unknown>(
      client.get({
        url: '/contact-center/voice/calls/browser-originate/capability',
        security: [{ scheme: 'bearer', type: 'http' }],
      }),
    ).then(parseBrowserOriginateProviderCapability);
  },

  prepareBrowserOriginatedCall(
    input: VoiceBrowserCallPreparationInput,
    activationProof: VoiceBrowserActivationProof,
  ): Promise<BrowserOriginatePreparedCall> {
    return unwrap<unknown>(
      client.post({
        url: '/contact-center/voice/calls/browser-originate',
        security: [{ scheme: 'bearer', type: 'http' }],
        headers: browserActivationHeaders(activationProof),
        body: browserCallPreparationBody(input),
      }),
    ).then((value) => {
      const prepared = parseBrowserOriginatePreparedCall(value);
      if (!prepared) {
        throw new VoiceRequestRejectedError(
          'The calling service returned an invalid Browser call authorization.',
          502,
          'browser_originate_contract_invalid',
        );
      }
      return prepared;
    });
  },

  /**
   * Hang up an active call. Spark VP tears down the call and Browser WebRTC
   * session.
   */
  hangUpCall(input: { connectorId: string; callSessionId: string }): Promise<{
    ok: true;
    state?: 'ended' | 'failed';
    resolution?: 'provider_confirmed' | 'stale_state_degraded';
  }> {
    return unwrap<{
      ok: true;
      state?: 'ended' | 'failed';
      resolution?: 'provider_confirmed' | 'stale_state_degraded';
    }>(
      sparkVpVoiceControllerHangupCall({
        body: { connectorId: input.connectorId },
        path: { callSessionId: input.callSessionId },
      }),
    );
  },

  rejectIncomingCall(input: {
    connectorId: string;
    callSessionId: string;
    participantId: string;
    endpointDeviceId: string;
  }): Promise<{
    ok: true;
    callSessionId: string;
    state: string;
    sequence: number;
  }> {
    return unwrap<{
      ok: true;
      callSessionId: string;
      state: string;
      sequence: number;
    }>(
      sparkVpVoiceControllerRejectInboundCall({
        path: { callSessionId: input.callSessionId },
        body: {
          connectorId: input.connectorId,
          participantId: input.participantId,
          endpointDeviceId: input.endpointDeviceId,
        },
      }),
    );
  },

  revokeBrowserSession(input: {
    connectorId: string;
    sessionId: string;
    activationProof: VoiceBrowserActivationProof;
  }): Promise<{ ok: true }> {
    return unwrap<{ ok: true }>(
      sparkVpVoiceControllerRevokeBrowserSession({
        path: { sessionId: input.sessionId },
        headers: browserActivationHeaders(input.activationProof),
      }),
    );
  },

  renewBrowserSession(
    sessionId: string,
    activationProof: VoiceBrowserActivationProof,
  ): Promise<VoiceBrowserSession> {
    return unwrap<unknown>(
      sparkVpVoiceControllerRenewBrowserSession({
        path: { sessionId },
        headers: browserActivationHeaders(activationProof),
      }),
    ).then(parseBrowserSession);
  },

  finalizeBrowserSessionRenewal(
    predecessorSessionId: string,
    activationProof: VoiceBrowserActivationProof,
  ): Promise<VoiceBrowserSessionRegistration> {
    return unwrap<unknown>(
      sparkVpVoiceControllerFinalizeBrowserSessionRenewal({
        path: { sessionId: predecessorSessionId },
        headers: browserActivationHeaders(activationProof),
      }),
    ).then((value) => parseBrowserSessionRegistration(value));
  },

  listLines(
    connectorId: string,
    signal?: AbortSignal,
  ): Promise<{ lines: VoiceLine[] }> {
    return unwrap<{ lines: VoiceLine[] }>(
      sparkVpVoiceControllerListLines({ query: { connectorId }, signal }),
    );
  },

  getActiveCalls(signal?: AbortSignal): Promise<{ calls: VoiceCallRecord[] }> {
    return unwrap<{ calls: Array<VoiceCallRecord & Record<string, unknown>> }>(
      voiceWorkspaceControllerActive({ signal }),
    ).then((result) => ({
      calls: normalizeCallRecords(result.calls),
    }));
  },

  listCalls(
    query: VoiceCallQuery = {},
    signal?: AbortSignal,
  ): Promise<{ calls: VoiceCallRecord[]; nextCursor: string | null }> {
    return unwrap<VoiceCallHistoryResponseDto>(
      voiceWorkspaceControllerListCalls({
        query,
        signal,
      }),
    ).then((result) => ({
      calls: normalizeCallRecords(result.calls),
      nextCursor: result.nextCursor,
    }));
  },

  getCallFilterOptions(
    signal?: AbortSignal,
  ): Promise<VoiceCallFilterOptionsResponseDto> {
    return unwrap<VoiceCallFilterOptionsResponseDto>(
      voiceWorkspaceControllerCallFilterOptions({ signal }),
    );
  },

  listMissedCalls(
    query: VoiceCallQuery = {},
    signal?: AbortSignal,
  ): Promise<{ calls: VoiceCallRecord[]; nextCursor: string | null }> {
    return unwrap<VoiceCallHistoryResponseDto>(
      voiceWorkspaceControllerMissed({
        query,
        signal,
      }),
    ).then((result) => ({
      calls: normalizeCallRecords(result.calls),
      nextCursor: result.nextCursor,
    }));
  },

  getCall(
    callSessionId: string,
    signal?: AbortSignal,
  ): Promise<{ call: VoiceCallRecord }> {
    return unwrap<{ call: VoiceCallRecord & Record<string, unknown> }>(
      voiceWorkspaceControllerDetail({ path: { callSessionId }, signal }),
    ).then((result) => ({
      call: normalizeCallRecord(result.call),
    }));
  },

  getCallDetailWorkspace(
    callSessionId: string,
    signal?: AbortSignal,
  ): Promise<{
    call: VoiceCallRecord | null;
    artifacts: VoiceArtifact[] | null;
    sectionErrors: VoiceCallDetailWorkspaceResponseDto['sectionErrors'];
  }> {
    return unwrap<VoiceCallDetailWorkspaceResponseDto>(
      voiceWorkspaceControllerDetailWorkspace({
        path: { callSessionId },
        signal,
      }),
    ).then((result) => ({
      call: result.call ? normalizeCallRecord(result.call) : null,
      artifacts: result.artifacts
        ? result.artifacts.map((artifact) =>
            normalizeArtifact(
              artifact as VoiceArtifact & Record<string, unknown>,
            ),
          )
        : null,
      sectionErrors: result.sectionErrors,
    }));
  },

  getPhonePolicy(
    normalizedPhone: string,
    signal?: AbortSignal,
  ): Promise<{ policy: VoicePhonePolicy }> {
    return unwrap<{
      policy: VoicePhonePolicy & {
        reason?: string | null;
        allowed?: boolean;
        doNotCall?: boolean;
        warningRequired?: boolean;
        reasons?: string[];
      };
    }>(
      voiceWorkspaceControllerPhonePolicy({
        path: { phone: normalizedPhone },
        signal,
      }),
    ).then(({ policy }) => ({
      policy: {
        ...policy,
        allowed:
          policy.allowed ??
          !['NOT_CONSENTED', 'DO_NOT_CALL'].includes(policy.consentStatus),
        doNotCall: policy.doNotCall ?? policy.consentStatus === 'DO_NOT_CALL',
        warningRequired: policy.warningRequired ?? false,
        reasons: policy.reasons ?? (policy.reason ? [policy.reason] : []),
      },
    }));
  },

  updatePhonePolicy(
    normalizedPhone: string,
    body: {
      consentStatus: VoicePhonePolicy['consentStatus'];
      reason?: string | null;
      notes?: string | null;
    },
  ): Promise<{ policy: VoicePhonePolicy }> {
    return unwrap<{ policy: VoicePhonePolicy }>(
      voiceWorkspaceControllerUpdatePhonePolicy({
        body: body as Parameters<
          typeof voiceWorkspaceControllerUpdatePhonePolicy
        >[0]['body'],
        path: { phone: normalizedPhone },
      }),
    );
  },

  savePostCall(
    callSessionId: string,
    body: {
      notes?: string;
      disposition?: string;
      afterCallStatus?: string;
      missedStatus?: string;
      callback?: {
        title?: string;
        description?: string;
        dueDate: string;
        assignedTenantMembershipId?: string;
      };
    },
  ): Promise<{ call: VoiceCallRecord; callbackTask?: { id: string } }> {
    return unwrap<{
      call: VoiceCallRecord & Record<string, unknown>;
      callbackTask?: { id: string };
    }>(
      voiceWorkspaceControllerUpdatePostCall({
        body: body as Parameters<
          typeof voiceWorkspaceControllerUpdatePostCall
        >[0]['body'],
        path: { callSessionId },
      }),
    ).then((result) => ({ ...result, call: normalizeCallRecord(result.call) }));
  },

  resolveMissedCall(
    callSessionId: string,
    missedStatus: string,
  ): Promise<{ call: VoiceCallRecord }> {
    return unwrap<{ call: VoiceCallRecord & Record<string, unknown> }>(
      voiceWorkspaceControllerUpdatePostCall({
        body: { missedStatus } as Parameters<
          typeof voiceWorkspaceControllerUpdatePostCall
        >[0]['body'],
        path: { callSessionId },
      }),
    ).then((result) => ({ call: normalizeCallRecord(result.call) }));
  },

  listCallLists(
    signal?: AbortSignal,
  ): Promise<{ callLists: VoiceCallList[]; nextCursor?: string | null }> {
    return unwrap<{
      callLists: Array<VoiceCallList & Record<string, unknown>>;
      nextCursor?: string | null;
    }>(voiceWorkspaceControllerListCallLists({ signal })).then((result) => ({
      ...result,
      callLists: result.callLists.map(normalizeCallList),
    }));
  },

  createCallList(body: {
    name: string;
    description?: string;
    lineId?: string | null;
    items: Array<{
      destinationE164: string;
      contactId?: string | null;
      dealId?: string | null;
    }>;
  }): Promise<{ callList: VoiceCallList }> {
    return unwrap<{ callList: VoiceCallList & Record<string, unknown> }>(
      voiceWorkspaceControllerCreateCallList({
        body: {
          name: body.name,
          description: body.description as never,
          defaultLineId: body.lineId ?? undefined,
          items: body.items.map((item) => ({
            destinationE164: item.destinationE164,
            contactId: item.contactId ?? undefined,
            dealId: item.dealId ?? undefined,
          })),
        },
      }),
    ).then((result) => ({ callList: normalizeCallList(result.callList) }));
  },

  previewCallList(
    destinations: string[],
    signal?: AbortSignal,
  ): Promise<VoiceCallListPreview> {
    return unwrap<VoiceCallListPreview>(
      voiceWorkspaceControllerPreviewCallList({
        body: { destinations },
        signal,
      }),
      'Call-list preview could not be generated.',
    );
  },

  getCallList(
    id: string,
    signal?: AbortSignal,
  ): Promise<{ callList: VoiceCallList }> {
    return unwrap<{ callList: VoiceCallList & Record<string, unknown> }>(
      voiceWorkspaceControllerGetCallList({ path: { id }, signal }),
    ).then((result) => ({
      callList: normalizeCallList(result.callList),
    }));
  },

  claimCallListItem(id: string): Promise<{ item: VoiceCallListItem | null }> {
    return unwrap<{
      item: (VoiceCallListItem & Record<string, unknown>) | null;
    }>(
      voiceWorkspaceControllerClaimCallListItem({ body: {}, path: { id } }),
    ).then((result) => ({
      item: result.item ? normalizeCallListItem(result.item) : null,
    }));
  },

  completeCallListItem(
    id: string,
    itemId: string,
    body: {
      leaseToken: string;
      disposition?: string;
      notes?: string;
      status?: 'COMPLETED' | 'SKIPPED';
      callbackDueDate?: string;
    },
  ): Promise<{ item: VoiceCallListItem }> {
    return unwrap<{ item: VoiceCallListItem & Record<string, unknown> }>(
      voiceWorkspaceControllerCompleteCallListItem({
        body: {
          leaseToken: body.leaseToken,
          status: body.status ?? 'COMPLETED',
          ...(body.disposition
            ? { disposition: body.disposition as never }
            : {}),
          ...(body.notes ? { notes: body.notes } : {}),
          ...(body.callbackDueDate
            ? { callback: { dueDate: body.callbackDueDate } }
            : {}),
        },
        path: { id, itemId },
      }),
    ).then((result) => ({ item: normalizeCallListItem(result.item) }));
  },

  getCallListProgress(
    id: string,
    signal?: AbortSignal,
  ): Promise<{
    progress: {
      total: number;
      completed: number;
      percentComplete: number;
      counts: Record<string, number>;
    };
  }> {
    return unwrap(
      voiceWorkspaceControllerCallListProgress({ path: { id }, signal }),
    );
  },

  getArtifacts(
    callSessionId: string,
    signal?: AbortSignal,
  ): Promise<{ artifacts: VoiceArtifact[] }> {
    return unwrap<{
      artifacts: Array<VoiceArtifact & Record<string, unknown>>;
    }>(
      voiceWorkspaceControllerListArtifacts({
        path: { callSessionId },
        signal,
      }),
    ).then((result) => ({
      artifacts: result.artifacts.map(normalizeArtifact),
    }));
  },

  getArtifact(
    artifactId: string,
    signal?: AbortSignal,
  ): Promise<{ artifact: VoiceArtifact }> {
    return unwrap<{ artifact: VoiceArtifact & Record<string, unknown> }>(
      voiceWorkspaceControllerArtifact({ path: { artifactId }, signal }),
    ).then((result) => ({
      artifact: normalizeArtifact(result.artifact),
    }));
  },

  getTranscriptContent(
    artifactId: string,
    signal?: AbortSignal,
  ): Promise<{
    transcript: {
      text: string | null;
      segments: import('./types').VoiceTranscriptSegment[];
    };
  }> {
    return unwrap(
      voiceWorkspaceControllerTranscriptContent({
        path: { artifactId },
        signal,
      }),
    );
  },

  getReportSummary(
    query: {
      from?: string;
      to?: string;
      agentMembershipId?: string;
      lineId?: string | null;
    },
    signal?: AbortSignal,
  ): Promise<{ report: VoiceReportSummary }> {
    type Aggregate = {
      calls: number;
      answered: number;
      missed: number;
      averageDurationSeconds: number;
      afterCallPending: number;
      afterCallCompleted: number;
      callbacks: number;
      name?: string;
    };
    type Report = Aggregate & {
      totalCalls: number;
      answeredCalls: number;
      missedCalls: number;
      averageAfterCallSeconds: number;
      callbackCompletionRate: number;
      agents: Array<Aggregate & { tenantMembershipId: string }>;
      lines: Array<Aggregate & { lineId: string; name?: string }>;
    };
    const reportQuery = { ...query, lineId: query.lineId ?? undefined };
    return unwrap<{ report: Report }>(
      voiceWorkspaceControllerReport({ query: reportQuery, signal }),
    ).then(({ report }) => ({
      report: {
        totalCalls: report.totalCalls,
        answeredCalls: report.answeredCalls,
        missedCalls: report.missedCalls,
        averageDurationSeconds: report.averageDurationSeconds,
        averageAfterCallSeconds: report.averageAfterCallSeconds,
        callbackCompletionRate: report.callbackCompletionRate,
        afterCallPending: report.afterCallPending,
        afterCallCompleted: report.afterCallCompleted,
        callbacks: report.callbacks,
        agents: report.agents.map((agent) => ({
          tenantMembershipId: agent.tenantMembershipId,
          name: agent.name ?? agent.tenantMembershipId,
          calls: agent.calls,
          answered: agent.answered,
          missed: agent.missed,
          averageDurationSeconds: agent.averageDurationSeconds,
        })),
        lines: report.lines.map((line) => ({
          lineId: line.lineId,
          name: line.name ?? line.lineId,
          calls: line.calls,
          answered: line.answered,
          missed: line.missed,
        })),
      },
    }));
  },
};
